import { Plus, Trash2 } from 'lucide-react';
import { Button, Input } from '@/components/ui';
import { useTranslation } from '@/lib/i18n';
import {
  UNLIMITED_MAX_BOUND,
  type ShippingFeeTier,
} from '../types/shippingFee';

interface ShippingFeeTierEditorProps {
  tiers: ShippingFeeTier[];
  /** 구간 단위(원/개/kg) 결정 — BY_AMOUNT/BY_QTY/BY_WEIGHT. */
  feeType: 'BY_AMOUNT' | 'BY_QTY' | 'BY_WEIGHT';
  disabled: boolean;
  onChange: (tiers: ShippingFeeTier[]) => void;
}

const emptyTier = (): ShippingFeeTier => ({
  minBound: 0,
  maxBound: UNLIMITED_MAX_BOUND,
  fee: 0,
  freeOverThreshold: null,
});

/** number 입력 파싱 — 빈/NaN 은 0. */
const num = (v: string): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** maxBound sentinel(무제한 상한)은 빈 입력으로 표기 — 운영자가 "상한 없음"을 직관 인지. */
const boundToInput = (v: number): string => (v >= UNLIMITED_MAX_BOUND ? '' : String(v));
const inputToMaxBound = (v: string): number =>
  v.trim() === '' ? UNLIMITED_MAX_BOUND : num(v);

const thresholdToInput = (v: number | null): string => (v == null ? '' : String(v));
const inputToThreshold = (v: string): number | null =>
  v.trim() === '' ? null : num(v);

/**
 * ID50 배송비 구간(BY_*) 동적 테이블. 각 구간은 [minBound, maxBound) 범위 + 요금 +
 * 무료전환 임계값을 가진다. 단위(원/개/kg)는 feeType 으로 결정. 무제한 상한은 빈
 * 입력으로 표기(저장 시 UNLIMITED_MAX_BOUND 로 보정).
 */
export const ShippingFeeTierEditor = ({
  tiers,
  feeType,
  disabled,
  onChange,
}: ShippingFeeTierEditorProps) => {
  const { t } = useTranslation('shippingFee');
  const unit = t(`shippingFee.tierUnit.${feeType}`);

  const updateTier = (index: number, patch: Partial<ShippingFeeTier>) => {
    onChange(tiers.map((t, i) => (i === index ? { ...t, ...patch } : t)));
  };

  const addTier = () => onChange([...tiers, emptyTier()]);

  const removeTier = (index: number) => onChange(tiers.filter((_, i) => i !== index));

  return (
    <div className="flex flex-col gap-2" data-testid="shipping-fee-tier-editor">
      <div
        className="overflow-hidden rounded-md"
        style={{ border: '1px solid var(--divider-soft)' }}
      >
        <table className="w-full border-collapse text-[12px]">
          <thead>
            <tr style={{ background: 'var(--surface-raised)' }}>
              <th
                className="px-3 py-2 text-left font-medium"
                style={{ color: 'var(--content-muted)' }}
              >
                {t('shippingFee.tierEditor.colMin', { unit })}
              </th>
              <th
                className="px-3 py-2 text-left font-medium"
                style={{ color: 'var(--content-muted)' }}
              >
                {t('shippingFee.tierEditor.colMax', { unit })}
              </th>
              <th
                className="px-3 py-2 text-left font-medium"
                style={{ color: 'var(--content-muted)' }}
              >
                {t('shippingFee.tierEditor.colFee')}
              </th>
              <th
                className="px-3 py-2 text-left font-medium"
                style={{ color: 'var(--content-muted)' }}
              >
                {t('shippingFee.tierEditor.colFreeOver', { unit })}
              </th>
              <th className="w-[44px] px-2 py-2" aria-hidden="true" />
            </tr>
          </thead>
          <tbody>
            {tiers.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-3 py-3 text-center"
                  style={{ color: 'var(--content-muted)' }}
                >
                  {t('shippingFee.tierEditor.empty')}
                </td>
              </tr>
            ) : null}
            {tiers.map((tier, ti) => (
              <tr
                key={tier.id ?? `new-${ti}`}
                style={{ borderTop: '1px solid var(--divider-soft)' }}
              >
                <td className="px-2 py-1.5">
                  <Input
                    type="number"
                    step="1"
                    min={0}
                    value={String(tier.minBound)}
                    onChange={(e) => updateTier(ti, { minBound: num(e.target.value) })}
                    disabled={disabled}
                    className="h-8 tabular-nums"
                    aria-label={t('shippingFee.tierEditor.minAria')}
                  />
                </td>
                <td className="px-2 py-1.5">
                  <Input
                    type="number"
                    step="1"
                    min={0}
                    value={boundToInput(tier.maxBound)}
                    onChange={(e) =>
                      updateTier(ti, { maxBound: inputToMaxBound(e.target.value) })
                    }
                    disabled={disabled}
                    placeholder={t('shippingFee.tierEditor.unlimitedPlaceholder')}
                    className="h-8 tabular-nums"
                    aria-label={t('shippingFee.tierEditor.maxAria')}
                  />
                </td>
                <td className="px-2 py-1.5">
                  <Input
                    type="number"
                    step="1"
                    min={0}
                    value={String(tier.fee)}
                    onChange={(e) => updateTier(ti, { fee: num(e.target.value) })}
                    disabled={disabled}
                    className="h-8 tabular-nums"
                    aria-label={t('shippingFee.tierEditor.feeAria')}
                  />
                </td>
                <td className="px-2 py-1.5">
                  <Input
                    type="number"
                    step="1"
                    min={0}
                    value={thresholdToInput(tier.freeOverThreshold)}
                    onChange={(e) =>
                      updateTier(ti, {
                        freeOverThreshold: inputToThreshold(e.target.value),
                      })
                    }
                    disabled={disabled}
                    placeholder={t('shippingFee.tierEditor.thresholdPlaceholder')}
                    className="h-8 tabular-nums"
                    aria-label={t('shippingFee.tierEditor.freeOverAria')}
                  />
                </td>
                <td className="px-2 py-1.5 text-center">
                  {!disabled ? (
                    <button
                      type="button"
                      onClick={() => removeTier(ti)}
                      aria-label={t('shippingFee.tierEditor.removeAria')}
                      style={{ color: 'var(--signal-danger)', cursor: 'pointer' }}
                    >
                      <Trash2 size={14} />
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!disabled ? (
        <div>
          <Button
            variant="outline"
            size="sm"
            onClick={addTier}
            data-testid="shipping-fee-add-tier"
          >
            <Plus size={14} aria-hidden="true" />
            {t('shippingFee.tierEditor.add')}
          </Button>
        </div>
      ) : null}
    </div>
  );
};
